import React from "react";
import { MdSearch } from "react-icons/md";

interface SearchFormProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onClear?: () => void;
  placeholder: string;
  /** Shows the Clear button and the "showing results for" line. */
  hasSearched?: boolean;
  loading?: boolean;
  /**
   * "inline" is the table header form (fixed width from sm up, smaller text);
   * "block" fills its container, for modal search panels.
   */
  variant?: "inline" | "block";
  /** Table headers show the active term through SearchResultsInfo instead. */
  showResultsLine?: boolean;
}

/**
 * The one search form.
 *
 * This markup was written out five times - four table headers plus
 * AssignmentSearch - with byte-identical submit and Clear buttons, and each
 * copy carried its own localSearchTerm state and submit/clear handlers.
 */
const SearchForm: React.FC<SearchFormProps> = ({
  value,
  onChange,
  onSubmit,
  onClear,
  placeholder,
  hasSearched = false,
  loading = false,
  variant = "inline",
  showResultsLine = false,
}) => (
  <>
    <form onSubmit={onSubmit} className="flex items-center space-x-2">
      <div
        className={`relative flex-1 ${variant === "inline" ? "sm:w-64" : ""}`}
      >
        <MdSearch
          className={`absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400 ${
            variant === "inline" ? "h-4 w-4" : "h-5 w-5"
          }`}
        />
        <input
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`focus:border-transparent w-full rounded-md border border-gray-300 py-2 pl-10 pr-4 focus:ring-2 focus:ring-biafit-primary dark:border-gray-600 dark:bg-navy-700 dark:text-white ${
            variant === "inline" ? "text-sm" : ""
          }`}
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        aria-label="Search"
        className="rounded-md bg-biafit-primary px-3 py-3 text-white hover:bg-biafit-secondary disabled:cursor-not-allowed disabled:opacity-50"
      >
        <MdSearch className="h-4 w-4" />
      </button>
      {hasSearched && onClear && (
        <button
          type="button"
          onClick={onClear}
          title="Clear search"
          className="rounded-md bg-gray-500 px-3 py-2 text-white hover:bg-gray-600"
        >
          Clear
        </button>
      )}
    </form>

    {showResultsLine && hasSearched && (
      <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
        Showing search results for "{value}"
      </div>
    )}
  </>
);

export default SearchForm;
